import React, { useLayoutEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import DoctorSidebar from "../components/DoctorSidebar/DoctorSidebar";
import PlatformMarqueeBanner from "../components/PlatformMarqueeBanner/PlatformMarqueeBanner";
import ProviderBottomNav from "./ProviderBottomNav";
import { tokenManager } from "../Api/tokenManager";
import "./DoctorLayout.scss";

export default function DoctorLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const providerRole = tokenManager.getActiveProviderRole();
  const hasProviderToken =
    providerRole === "hospital"
      ? tokenManager.getTokenForRole("hospital")
      : tokenManager.getTokenForRole("doctor");
  const loginPath = providerRole === "hospital" ? "/hospital-login" : "/doctor-login";
  const isHospital = providerRole === "hospital";
  const base = isHospital ? "/hospital" : "/doctor";
  const notificationsPath = `${base}-notifications`;

  const appbarMeta = useMemo(() => {
    const p = String(location.pathname || "").toLowerCase();
    if (p === `${base}-dashboard` || p === `${base}-bids`) {
      return { title: "Patient Bids", subtitle: "Review active bids and take action quickly" };
    }
    if (p === `${base}-confirmed-patients`) return { title: "Confirmed Patients", subtitle: "Manage confirmed appointments and patient details" };
    if (p === `${base}-appointments`)
      return {
        title: "Appointments",
        subtitle: "Booked and confirmed visits with procedure and offer details",
      };
    if (p === `${base}-chats`)
      return {
        title: isHospital ? "Hospital Chats" : "Doctor Chats",
        subtitle: "Open a bid conversation with patients",
      };
    if (p === `${base}-availability`) return { title: "Manage Availability", subtitle: "Set clinic schedules and slot preferences" };
    if (p === `${base}-notifications`) return { title: "Notifications", subtitle: "Realtime updates and admin alerts" };
    if (p === `${base}-settings`) return { title: "Settings", subtitle: "Profile, specialties, and account preferences" };
    return { title: "HealBid", subtitle: isHospital ? "Hospital workspace" : "Doctor workspace" };
  }, [base, isHospital, location.pathname]);

  useLayoutEffect(() => {
    if (!hasProviderToken) {
      navigate(loginPath, { replace: true });
    }
  }, [hasProviderToken, loginPath, navigate]);

  if (!hasProviderToken) {
    return null;
  }

  return (
    <div className="doctor_layout_wrap">
      <PlatformMarqueeBanner />
      <div className="doctor_layout">
        <DoctorSidebar />

        <div className="doctor_main_section">
          <div className="doctor_appbar">
            <div className="doctor_appbar_left">
              <div className="doctor_appbar_text">
                <h2>{appbarMeta.title}</h2>
                <p>{appbarMeta.subtitle}</p>
              </div>
            </div>
            <div className="doctor_appbar_right">
              <button
                type="button"
                className={`doctor_appbar_notif ${location.pathname === notificationsPath ? "is-active" : ""}`.trim()}
                aria-label="Open notifications"
                onClick={() => navigate(notificationsPath)}
              >
                <Bell size={18} strokeWidth={2.2} />
              </button>
            </div>
          </div>
          <div className="doctor_page_content">{children}</div>
        </div>
      </div>
      <ProviderBottomNav basePath={base} />
    </div>
  );
}